import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, CheckIcon, ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { audienceMemberApi, audienceApi } from '../../api';

const CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'];
const MEMBER_TYPES = [
  { value: 'AUTO_SHOP', label: 'Auto Shop' },
  { value: 'ITP_STATION', label: 'ITP Station' },
];

const FILTER_MEMBER_TYPES = [
  { value: '', label: 'All Types' },
  { value: '0', label: 'Auto Shop' },
  { value: '1', label: 'ITP Station' },
];

const FILTER_INTEREST_LEVELS = [
  { value: '', label: 'All Interest' },
  { value: '0', label: 'Undefined' },
  { value: '1', label: 'Not Interested' },
  { value: '2', label: 'Interested' },
  { value: '3', label: 'Very Interested' },
];

const FILTER_STATUS_LEVELS = [
  { value: '', label: 'All Status' },
  { value: '0', label: 'Not Contacted' },
  { value: '1', label: 'Contacted' },
  { value: '2', label: 'Not answered' },
  { value: '3', label: 'Requires Action' },
  { value: '4', label: 'Paying User' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type' },
  { value: 'interest', label: 'Interest' },
  { value: 'status', label: 'CRM Status' },
  { value: 'turnover', label: 'Turnover' },
];

const AudienceForm = ({ show, audience, members, onSubmit, onClose, onCsvImport }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    channel: 'EMAIL',
    audienceMembers: [],
    memberType: 'AUTO_SHOP',
  });
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [creationMode, setCreationMode] = useState('manual');
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [audienceOptions, setAudienceOptions] = useState([]);
  const [filterData, setFilterData] = useState({
    query: '',
    type: '',
    interest: '',
    status: '',
    currentSoft: '',
    notInAudienceId: '',
    minTurnover: '',
    maxTurnover: '',
    sortBy: 'created_at',
    sortAsc: false,
  });

  const [memberPage, setMemberPage] = useState(1);
  const [paginatedMembers, setPaginatedMembers] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    if (audience) {
      setFormData({
        name: audience.name || '',
        description: audience.description || '',
        channel: audience.channel || 'EMAIL',
        audienceMembers: audience.audienceMembers || [],
        memberType: 'AUTO_SHOP',
      });
      setSelectedMembers(audience.audienceMembers || []);
    } else {
      setFormData({
        name: '',
        description: '',
        channel: 'EMAIL',
        audienceMembers: [],
        memberType: 'AUTO_SHOP',
      });
      setSelectedMembers([]);
    }

    setCreationMode('manual');
    setCsvFile(null);
    setMemberPage(1);
    setFilterData({
      query: '',
      type: '',
      interest: '',
      status: '',
      currentSoft: '',
      notInAudienceId: '',
      minTurnover: '',
      maxTurnover: '',
      sortBy: 'created_at',
      sortAsc: false,
    });
  }, [audience]);

  const fetchMembers = useCallback(async () => {
    if (!show || creationMode !== 'manual') return;
    setLoadingMembers(true);
    try {
      const response = await audienceMemberApi.getAllPaginated(memberPage, pageSize, memberSearch);
      setPaginatedMembers(response.members || []);
      setTotalMembers(response.total || 0);
    } catch (error) {
      console.error('Error fetching members:', error);
      setPaginatedMembers(members || []);
      setTotalMembers(members?.length || 0);
    } finally {
      setLoadingMembers(false);
    }
  }, [show, creationMode, memberPage, memberSearch, members]);

  const fetchSoftwareOptions = useCallback(async () => {
    if (!show || audience) return;
    try {
      const values = await audienceMemberApi.getDistinctSoftware();
      setSoftwareOptions(Array.isArray(values) ? values : []);
    } catch (error) {
      console.error('Error fetching software options:', error);
      setSoftwareOptions([]);
    }
  }, [show, audience]);

  const fetchAudienceOptions = useCallback(async () => {
    if (!show || audience) return;
    try {
      const response = await audienceApi.getAll(1, 200, '');
      const list = Array.isArray(response)
        ? response
        : (response?.audiences || response?.items || response?.content || []);
      setAudienceOptions(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching audience options:', error);
      setAudienceOptions([]);
    }
  }, [show, audience]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    fetchSoftwareOptions();
  }, [fetchSoftwareOptions]);

  useEffect(() => {
    fetchAudienceOptions();
  }, [fetchAudienceOptions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMemberPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      alert('Name is required');
      return;
    }

    if (creationMode === 'csv' && csvFile) {
      setImporting(true);
      try {
        if (onCsvImport) {
          await onCsvImport(csvFile, formData.name, formData.description, formData.channel, formData.memberType);
        }
      } finally {
        setImporting(false);
      }
      return;
    }

    if (creationMode === 'filters' && !audience) {
      onSubmit({
        ...formData,
        audienceMembers: [],
        useFilters: true,
        filters: {
          query: filterData.query || null,
          type: filterData.type === '' ? null : Number(filterData.type),
          interest: filterData.interest === '' ? null : Number(filterData.interest),
          status: filterData.status === '' ? null : Number(filterData.status),
          currentSoft: filterData.currentSoft || null,
          notInAudienceId: filterData.notInAudienceId || null,
          minTurnover: filterData.minTurnover === '' ? null : filterData.minTurnover,
          maxTurnover: filterData.maxTurnover === '' ? null : filterData.maxTurnover,
          sortBy: filterData.sortBy || 'created_at',
          sortAsc: Boolean(filterData.sortAsc),
        },
      });
      return;
    }

    const submitData = {
      ...formData,
      audienceMembers: selectedMembers,
    };

    if (audience) {
      submitData.id = audience.id;
    }

    onSubmit(submitData);
  };

  const toggleMember = (memberId) => {
    setSelectedMembers((prev) => (
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    ));
  };

  const selectAllOnPage = () => {
    const pageIds = paginatedMembers.map((m) => m.id);
    setSelectedMembers((prev) => {
      const newSet = new Set(prev);
      pageIds.forEach((id) => newSet.add(id));
      return Array.from(newSet);
    });
  };

  const deselectAll = () => {
    setSelectedMembers([]);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setCsvFile(selectedFile);
    }
  };

  const totalPages = Math.ceil(totalMembers / pageSize);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {audience ? 'Edit Audience' : 'Create Audience'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              required
              className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Q4 Newsletter Subscribers"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              rows={2}
              className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Channel *</label>
            <div className="mt-2 flex gap-4">
              {CHANNELS.map((channel) => (
                <label key={channel} className="flex items-center">
                  <input
                    type="radio"
                    name="channel"
                    value={channel}
                    checked={formData.channel === channel}
                    onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">{channel}</span>
                </label>
              ))}
            </div>
          </div>

          {!audience && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="mb-2 text-sm font-medium text-gray-700">Creation Mode</p>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center cursor-pointer">
                  <input type="radio" name="creationMode" value="manual" checked={creationMode === 'manual'} onChange={(e) => setCreationMode(e.target.value)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300" />
                  <span className="ml-2 text-sm text-gray-700">Manual Selection</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input type="radio" name="creationMode" value="csv" checked={creationMode === 'csv'} onChange={(e) => setCreationMode(e.target.value)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300" />
                  <span className="ml-2 text-sm text-gray-700">Import from CSV</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input type="radio" name="creationMode" value="filters" checked={creationMode === 'filters'} onChange={(e) => setCreationMode(e.target.value)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300" />
                  <span className="ml-2 text-sm text-gray-700">Create with Filters</span>
                </label>
              </div>
            </div>
          )}

          {creationMode === 'csv' && !audience ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Format</h4>
                <p className="text-sm text-blue-700 mb-2">Your CSV file should have the following columns:</p>
                <code className="text-xs bg-blue-100 px-2 py-1 rounded block">name,email,phone,cui</code>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Member Type *</label>
                <p className="text-xs text-gray-500 mb-2">All new members created from this import will have this type.</p>
                <div className="mt-2 flex gap-4">
                  {MEMBER_TYPES.map((typeOption) => (
                    <label key={typeOption.value} className="flex items-center">
                      <input
                        type="radio"
                        name="memberType"
                        value={typeOption.value}
                        checked={formData.memberType === typeOption.value}
                        onChange={(e) => setFormData({ ...formData, memberType: e.target.value })}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">{typeOption.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select CSV File</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {csvFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <DocumentTextIcon className="h-8 w-8 text-green-500" />
                        <span className="text-sm text-gray-700">{csvFile.name}</span>
                      </div>
                    ) : (
                      <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                    )}
                    <div className="flex text-sm text-gray-600 justify-center">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-purple-600 hover:text-purple-500">
                        <span>{csvFile ? 'Change file' : 'Upload a file'}</span>
                        <input type="file" accept=".csv" onChange={handleFileChange} className="sr-only" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : creationMode === 'filters' && !audience ? (
            <div className="space-y-4 rounded-md border border-purple-100 bg-purple-50 p-4">
              <p className="text-sm font-medium text-gray-900">Auto-populate audience from filters</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Query</label>
                <input type="text" value={filterData.query} onChange={(e) => setFilterData((prev) => ({ ...prev, query: e.target.value }))} placeholder="Name, email, phone, or CUI" className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={filterData.type} onChange={(e) => setFilterData((prev) => ({ ...prev, type: e.target.value }))} className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm">
                    {FILTER_MEMBER_TYPES.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interest</label>
                  <select value={filterData.interest} onChange={(e) => setFilterData((prev) => ({ ...prev, interest: e.target.value }))} className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm">
                    {FILTER_INTEREST_LEVELS.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CRM Status</label>
                  <select value={filterData.status} onChange={(e) => setFilterData((prev) => ({ ...prev, status: e.target.value }))} className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm">
                    {FILTER_STATUS_LEVELS.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Software</label>
                  <input type="text" value={filterData.currentSoft} onChange={(e) => setFilterData((prev) => ({ ...prev, currentSoft: e.target.value }))} list="audience-filter-software-options" placeholder="ServiceM" className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm" />
                  <datalist id="audience-filter-software-options">
                    {softwareOptions.map((software) => <option key={software} value={software} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exclude Members Already in Audience</label>
                  <select
                    value={filterData.notInAudienceId}
                    onChange={(e) => setFilterData((prev) => ({ ...prev, notInAudienceId: e.target.value }))}
                    className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    <option value="">No audience exclusion</option>
                    {audienceOptions.map((audienceOption) => (
                      <option key={audienceOption.id} value={audienceOption.id}>
                        {audienceOption.name || audienceOption.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Turnover</label>
                  <input type="number" min="0" value={filterData.minTurnover} onChange={(e) => setFilterData((prev) => ({ ...prev, minTurnover: e.target.value }))} placeholder="100000" className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Turnover</label>
                  <input type="number" min="0" value={filterData.maxTurnover} onChange={(e) => setFilterData((prev) => ({ ...prev, maxTurnover: e.target.value }))} placeholder="500000" className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <select value={filterData.sortBy} onChange={(e) => setFilterData((prev) => ({ ...prev, sortBy: e.target.value }))} className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm">
                    {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div className="flex items-center pt-6">
                  <label className="inline-flex items-center text-sm text-gray-700">
                    <input type="checkbox" checked={filterData.sortAsc} onChange={(e) => setFilterData((prev) => ({ ...prev, sortAsc: e.target.checked }))} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" />
                    <span className="ml-2">Sort Ascending</span>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Members ({selectedMembers.length} selected of {totalMembers} total)</label>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllOnPage} className="text-xs text-purple-600 hover:text-purple-800">Select All on Page</button>
                  <span className="text-gray-300">|</span>
                  <button type="button" onClick={deselectAll} className="text-xs text-gray-600 hover:text-gray-800">Deselect All</button>
                </div>
              </div>

              <input type="text" className="mb-2 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm" placeholder="Search members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />

              <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                {loadingMembers ? (
                  <p className="p-4 text-sm text-gray-500 text-center">Loading members...</p>
                ) : paginatedMembers.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">No members available. Add members first.</p>
                ) : (
                  paginatedMembers.map((member) => {
                    const info = member.memberInfo || {};
                    const isSelected = selectedMembers.includes(member.id);
                    return (
                      <div key={member.id} onClick={() => toggleMember(member.id)} className={`flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${isSelected ? 'bg-purple-50' : ''}`}>
                        <div className="flex items-center">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 ${isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                            {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{info.name}</p>
                            <p className="text-xs text-gray-500">{info.email}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {totalPages > 1 && (
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-500">Page {memberPage} of {totalPages}</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setMemberPage((p) => Math.max(1, p - 1))} disabled={memberPage === 1} className="px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                    <button type="button" onClick={() => setMemberPage((p) => Math.min(totalPages, p + 1))} disabled={memberPage === totalPages} className="px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={importing || (creationMode === 'csv' && !csvFile)} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed">
              {importing ? 'Importing...' : audience ? 'Update' : creationMode === 'csv' ? 'Import & Create' : creationMode === 'filters' ? 'Create with Filters' : 'Create'} Audience
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AudienceForm;

